import type { ReactNode } from "react";
import Link from "next/link";
import type { Theme } from "@cw/design-tokens/theme";

export type LinkStyle = "primary" | "secondary" | "ghost";

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-[var(--radius-button,999px)] font-sans text-sm font-semibold " +
  "transition-all duration-200 ease-out-soft disabled:cursor-not-allowed disabled:opacity-60";
const SIZES = { md: "px-6 py-3", lg: "px-8 py-4 text-base" } as const;

function variantClasses(style: LinkStyle, theme: Theme): string {
  const onDark = theme === "ink" || theme === "accent";
  // Primary is the one filled call-to-action colour per brand (--color-cta); secondary is the quieter ink fill.
  if (style === "primary") {
    return "bg-cta text-cta-ink hover:bg-cta-hover shadow-card hover:shadow-card-hover";
  }
  if (style === "secondary") {
    if (onDark) return "bg-paper-raised text-ink hover:bg-white/90 shadow-card";
    return "bg-ink text-paper-raised hover:bg-ink-2 shadow-card hover:shadow-card-hover";
  }
  if (onDark) return "text-paper-raised ring-1 ring-inset ring-white/35 hover:bg-white/10";
  return "text-ink ring-1 ring-inset ring-hairline hover:text-accent-ink hover:ring-accent-ink";
}

export function isExternal(href: string): boolean {
  return /^(https?:|mailto:|tel:)/i.test(href);
}

export interface ButtonLinkProps {
  href: string;
  children: ReactNode;
  style?: LinkStyle;
  theme?: Theme;
  size?: keyof typeof SIZES;
  className?: string;
}

export function ButtonLink({ href, children, style = "primary", theme = "paper", size = "md", className = "" }: ButtonLinkProps) {
  const classes = `${BASE} ${SIZES[size]} ${variantClasses(style, theme)} ${className}`.trim();
  if (isExternal(href)) {
    const newTab = href.startsWith("http");
    return (
      <a href={href} className={classes} {...(newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
        {children}
      </a>
    );
  }
  return <Link href={href} className={classes}>{children}</Link>;
}

export interface ButtonProps {
  children: ReactNode;
  type?: "button" | "submit";
  style?: LinkStyle;
  theme?: Theme;
  size?: keyof typeof SIZES;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
}

export function Button({ children, type = "button", style = "primary", theme = "paper", size = "md", disabled = false, onClick, className = "" }: ButtonProps) {
  return (
    <button type={type} disabled={disabled} onClick={onClick} className={`${BASE} ${SIZES[size]} ${variantClasses(style, theme)} ${className}`.trim()}>
      {children}
    </button>
  );
}

export interface LinkSpec { label: string; href: string; style?: LinkStyle }

export function LinkRow({ links, theme = "paper", size = "md", className = "" }: { links?: LinkSpec[]; theme?: Theme; size?: keyof typeof SIZES; className?: string }) {
  const usable = (links ?? []).filter((l) => l.label && l.href);
  if (usable.length === 0) return null;
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`.trim()}>
      {usable.map((l, i) => (
        <ButtonLink key={`${l.label}-${i}`} href={l.href} style={l.style ?? (i === 0 ? "primary" : "ghost")} theme={theme} size={size}>
          {l.label}
        </ButtonLink>
      ))}
    </div>
  );
}
